import React, {useEffect, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from '../../../../modules/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {useNavigate} from 'react-router-dom'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {saveShortcutIntegration} from 'app/Integrations/Shortcut'
import DrawerHeader from '../drawer-components/DrawerHeader'
import { DrawerContent } from '../drawer-components/DrawerContent'
import DrawerFooter from '../drawer-components/DrawerFooter'
import { AddDrawerIntegrationProps } from '../utils/types'

const ShortcutDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const [shortcutApiToken, setShortcutApiToken] = useState('')
  const {currentUser} = useAuth()
  const [integration, setIntegration] = useState<any[]>([])
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected'
  const navigate = useNavigate()
  const [selectedTeam, setSelectedTeam] = useState('')
  const [teams, setTeams] = useState<any[]>([])

  useEffect(() => {
    if (isOpen && id) {
      getIntegration()
    }
  }, [id, isOpen])

  useEffect(() => {
    if (isConnected && integration[0]) {
      setShortcutApiToken(integration[0].apiToken || '')
    } else {
      setShortcutApiToken('')
    }
  }, [integration, isConnected])

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'Shortcut') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              apiToken: data.apiToken,
              teams: data.teams,
            }
            setIntegration([integrationData])
            setTeams(data.teams || [])
            setSelectedTeam(data.project_id || '')
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  const validateToken = async (
    token: string
  ): Promise<{isValid: boolean; projects?: any[]; teams?: any; workflows?:any}> => {
    try {
      const response = await fetch(
        'https://us-central1-slate-134a0.cloudfunctions.net/shortcutAuth',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({token}),
        }
      )

      if (!response.ok) {
        throw new Error('Failed to validate token')
      }

      const data = await response.json()
      return {
        isValid: data.isValid,
        projects: data.projects,
        teams: data.teams,
        workflows:data.workflows,
      }
    } catch (error) {
      console.error('Error validating Shortcut token:', error)
      return {isValid: false}
    }
  }

  const handleConnect = async () => {
    if (!shortcutApiToken) {
      showErrorToast({message: 'Please enter a valid API token'})
      return
    }

    setIsLoading(true)
    const validationResult = await validateToken(shortcutApiToken)
    if (validationResult.isValid) {
      try {
        const integrationData = {
          apiToken: shortcutApiToken,
          tenantId: currentUser?.tenantId || '',
          uid: currentUser?.uid || '',
          userName: currentUser?.userName || '',
          email: currentUser?.email || '',
          projects: validationResult.projects || [],
          teams: validationResult.teams || [],
          workflows:validationResult.workflows || [],
        }
        localStorage.setItem('shortcut_access_token', shortcutApiToken)
        const result = await saveShortcutIntegration(integrationData, currentUser)

        if (result.success) {
          showSuccessToast({message: 'Successfully connected to Shortcut'})
          onClose()
          navigate('/integrations')
        } else {
          showErrorToast({message: result.error || 'Failed to connect to Shortcut'})
        }
      } catch (error) {
        showErrorToast({message: 'Failed to connect to Shortcut'})
      }
    } else {
      showErrorToast({message: 'Invalid API token. Please check and try again.'})
    }
    setIsLoading(false)
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!id || !tenantId) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      await deleteDoc(integrationRef)
      const tenantRef = doc(db, 'tenants', tenantId)
      const vendorsRef = collection(tenantRef, 'vendors')
      const VendorQuery = query(vendorsRef, where('vendor_name', '==', 'Shortcut'))
      const VendorSnapshot = await getDocs(VendorQuery)

      const deleteVendorPromises = VendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })
      await Promise.all([...deleteVendorPromises])
      localStorage.removeItem('shortcut_access_token')
      showSuccessToast({message: 'Successfully disconnected from Shortcut'})
      setIntegration([])
      onClose()
    } catch (error) {
      showErrorToast({message: 'Failed to disconnect from Shortcut'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleTeamSelect = async (teamId: string) => {
    if (!id) {
      return
    }

    const currentDate = new Date()
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1
    const day = currentDate.getDate()
    const todaysDate = month + '/' + day + '/' + year

    try {
      if (currentUser) {
        const tenantId = currentUser.tenantId || ''
        const docRef = doc(db, 'tenants', tenantId, 'integrations', id)

        await updateDoc(docRef, {project_id: teamId})

        const tenantRef = doc(db, 'tenants', currentUser.tenantId!)
        const activitiesRef = collection(tenantRef, 'activities')
        const selectedTeamName = teams.find((team) => team.id === teamId)?.name || teamId

        await addDoc(activitiesRef, {
          message: `${currentUser?.userName} has updated the ${selectedTeamName} team in the Shortcut integration.`,
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          action: 'Shortcut update',
          target: 'Integration',
          email: currentUser?.email,
          username: currentUser?.userName,
          date: todaysDate,
        })

        showSuccessToast({message: 'Shortcut team updated successfully'})
      }
    } catch (error) {
      showErrorToast({message: 'Failed to update team'})
    } finally {
    }
  }

  return (
    <Drawer open={isOpen} onClose={onClose} direction='right' className='' size='45%'>
      <DrawerHeader title='Shortcut' onClose={onClose} />

      <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className='form'>
        <DrawerContent
            title='Shortcut'
            description='Project Management for Software Teams'
            logo='/media/integrations/Shortcut.avif'
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo='SecureSlate will be granted access to your Shortcut account using the provided API
              token.'
            accessTypeinfo='You need to provide a valid API token generated from your Shortcut account settings.'
          />

          <div className='mb-4'>
            <label className='form-label'>API Token</label>
            <input
              type='password'
              className='form-control'
              placeholder='Enter your Shortcut API token'
              value={shortcutApiToken}
              onChange={(e) => setShortcutApiToken(e.target.value)}
              disabled={isConnected}
            />
          </div>
          <div className='mb-10'>
            <label className='form-label'>Select Team</label>
            <select
              className='form-select'
              value={selectedTeam}
              onChange={(e) => {
                setSelectedTeam(e.target.value)
                handleTeamSelect(e.target.value)
              }}
              disabled={!isConnected}
            >
              <option value=''>Select a team</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  )
}

export default ShortcutDrawer
